import * as React from "react";
import {
  List,
  Datagrid,
  EditButton,
  TextInput,
  FilterLiveForm,
  TopToolbar,
  CreateButton,
  ExportButton,
  useListContext,
  useResourceDefinition,
  ReferenceInput,
  AutocompleteInput,
  useInput,
  type AutocompleteInputProps,
  type ListProps,
  type DatagridProps,
  type RaRecord,
  type InputProps,
  type ReferenceInputProps,
  AutocompleteArrayInput,
} from "react-admin";
import { Box, Card, CardContent, Stack, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { layout } from "./tokens";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

export const compactFieldSx = {
  "& .MuiFormControl-root": {
    m: 0,
  },
  "& .MuiInputBase-root": {
    height: 40,
    alignItems: "center",
    backgroundColor: "common.white",
    borderRadius: 1,
    fontSize: 14,
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    height: "40px",
    boxSizing: "border-box",
    fontSize: 14,
  },
  "& .MuiInputLabel-root": {
    fontSize: 14,
  },
  "& .MuiFormHelperText-root": {
    display: "none",
  },
  "& .MuiPickersInputBase-sectionsContainer": {
    height: "40px",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },

  "& .MuiPickersSectionList-section": {
    fontSize: 14,
    lineHeight: "40px",
  },

  "& .MuiInputAdornment-root": {
    marginRight: 1,
    marginLeft: 0.5,
  },

  "& .MuiIconButton-root": {
    padding: 0.5,
    borderRadius: 1,
  },

  "& .MuiIconButton-root .MuiSvgIcon-root": {
    fontSize: 18,
  },
};

type AppListProps = ListProps & {
  children: React.ReactNode;
};

export function AppList({ children, actions, ...props }: AppListProps) {
  return (
    <List
      {...props}
      actions={actions ?? false}
      sx={{
        px: layout.page.paddingX,
        pb: layout.page.paddingY,
        "& .RaList-main": {
          width: "100%",
        },
        "& .RaList-content": {
          boxShadow: "none",
          background: "transparent",
        },
        "& .RaList-noResults": {
          mt: 0,
        },
      }}
    >
      <Stack spacing={layout.list.gap}>{children}</Stack>
    </List>
  );
}

export function AppListCard({ children }: { children: React.ReactNode }) {
  return (
    <Card
      sx={{
        borderRadius: layout.card.radius,
        overflow: "hidden",
        boxShadow: "none",
        backgroundImage: "none",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {children}
    </Card>
  );
}

export function AppListActions({ children }: { children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
      }}
    >
      <TopToolbar
        sx={{
          minHeight: 0,
          p: 0,
          gap: layout.list.actionsGap,
          "& .MuiButton-root": {
            textTransform: "none",
          },
        }}
      >
        {children}
      </TopToolbar>
    </Box>
  );
}

type AppListDefaultActionsProps = {
  hasCreate?: boolean;
  hasExport?: boolean;
};

export function AppListDefaultActions({
  hasCreate = true,
  hasExport = true,
}: AppListDefaultActionsProps) {
  const { exporter, total } = useListContext();
  const resourceDefinition = useResourceDefinition();
  const canCreate = hasCreate ?? resourceDefinition.hasCreate;

  return (
    <AppListActions>
      {hasExport && exporter !== false ? (
        <ExportButton disabled={total === 0} />
      ) : null}
      {canCreate ? <CreateButton /> : null}
    </AppListActions>
  );
}

export function AppListFilterCard({
  search,
  filters,
}: {
  search?: React.ReactNode;
  filters?: React.ReactNode;
}) {
  return (
    <AppListCard>
      <CardContent
        sx={{
          px: layout.list.filtersPaddingX,
          py: layout.list.filtersPaddingY,
          "&:last-child": {
            pb: layout.list.filtersPaddingY,
          },
        }}
      >
        <FilterLiveForm>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: "0 1 auto",
                minWidth: 0,
              }}
            >
              {search}
            </Box>
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                "& .RaInput-root": {
                  m: 0,
                },
                "& .MuiFormControl-root": {
                  m: 0,
                },
              }}
            >
              {filters}
            </Stack>
          </Box>
        </FilterLiveForm>
      </CardContent>
    </AppListCard>
  );
}

type AppDatagridProps = DatagridProps & {
  children: React.ReactNode;
};

export function AppDatagrid({ children, ...props }: AppDatagridProps) {
  return (
    <AppListCard>
      <Box sx={{ overflowX: "auto" }}>
        <Datagrid
          {...props}
          bulkActionButtons={false}
          rowClick={props.rowClick ?? "show"}
          sx={{
            minWidth: layout.list.tableMinWidth,
            "& .RaDatagrid-table": {
              borderCollapse: "separate",
              borderSpacing: 0,
            },
            "& .RaDatagrid-headerRow": {
              height: layout.list.headerHeight,
            },
            "& .RaDatagrid-headerCell": {
              whiteSpace: "nowrap",
              fontWeight: 600,
              color: "text.primary",
              backgroundColor: "action.hover",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            },
            "& .RaDatagrid-rowCell": {
              verticalAlign: "middle",
            },
            "& .RaDatagrid-row": {
              "&:last-of-type .RaDatagrid-rowCell": {
                borderBottom: "none",
              },
            },
            "& .RaDatagrid-row:hover": {
              cursor: "pointer",
            },
          }}
        >
          {children}
        </Datagrid>
      </Box>
    </AppListCard>
  );
}

export function ListEditButton() {
  return <EditButton label="" sx={{ minWidth: 0 }} />;
}

type AppRowActionsProps = {
  children: React.ReactNode;
};

export function AppRowActions({ children }: AppRowActionsProps) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
      }}
    >
      {children}
    </Box>
  );
}

export function SearchFilterInput(
  props: React.ComponentProps<typeof TextInput>,
) {
  return (
    <TextInput
      {...props}
      resettable
      variant="outlined"
      size="small"
      helperText={false}
      sx={{
        width: layout.list.searchWidth,
        maxWidth: "100%",
        ...compactFieldSx,
        ...props.sx,
      }}
      slotProps={{
        ...props.slotProps,
        inputLabel: {
          ...props.slotProps?.inputLabel,
          shrink: true,
        },
        input: {
          ...props.slotProps?.input,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export function AppFilterInputBox({
  children,
  width = 260,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <Box
      sx={{
        width,
        maxWidth: "100%",
        "& .RaInput-root": {
          width: "100%",
          m: 0,
        },
        "& .MuiFormControl-root": {
          width: "100%",
          m: 0,
        },
        "& .MuiInputBase-root": {
          height: 40,
        },
      }}
    >
      {children}
    </Box>
  );
}

type ReferenceFilterInputProps = ReferenceInputProps & {
  autocompleteProps?: AutocompleteInputProps;
  width?: number;
};

export function ReferenceFilterInput({
  width = 240,
  autocompleteProps,
  ...props
}: ReferenceFilterInputProps) {
  return (
    <Box
      sx={{
        width,
        maxWidth: "100%",
        ...compactFieldSx,
      }}
    >
      <ReferenceInput {...props}>
        <AutocompleteInput
          size="small"
          fullWidth
          variant="outlined"
          helperText={false}
          label={props.label}
          sx={{
            ...compactFieldSx,
            "& .MuiAutocomplete-input": {
              py: "0 !important",
            },
          }}
          {...autocompleteProps}
        />
      </ReferenceInput>
    </Box>
  );
}

export function SelectArrayFilterInput(
  props: React.ComponentProps<typeof AutocompleteArrayInput>,
) {
  return (
    <AutocompleteArrayInput
      {...props}
      helperText={false}
      label={props.label}
      fullWidth
      size="small"
      variant="outlined"
      clearOnBlur={false}
      blurOnSelect={false}
      openOnFocus
      slotProps={{
        ...props.slotProps,
        chip: {
          ...props.slotProps?.chip,
          size: "small",
        },
      }}
      sx={{
        width: layout.list.filterMinWidth,
        maxWidth: "100%",
        ...compactFieldSx,

        "& .MuiInputLabel-root": {
          transform: "translate(14px, -9px) scale(0.75)",
          transformOrigin: "top left",
          backgroundColor: "common.white",
          px: 0.5,
          maxWidth: "calc(100% - 24px)",
        },

        "& .MuiAutocomplete-inputRoot": {
          minHeight: 40,
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: 0.5,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          paddingTop: "7px !important",
          paddingBottom: "7px !important",
          paddingLeft: "8px !important",
          paddingRight: "36px !important",
        },

        "& .MuiAutocomplete-tag": {
          flex: "0 0 auto",
          height: 24,
          margin: 0,
          borderRadius: 1,
          maxWidth: "none",
        },

        "& .MuiAutocomplete-tag .MuiChip-label": {
          px: 1,
          fontSize: 12,
          lineHeight: "24px",
          whiteSpace: "nowrap",
        },

        "& .MuiAutocomplete-tag .MuiChip-deleteIcon": {
          mr: 0.25,
          ml: -0.25,
          fontSize: 18,
        },

        "& .MuiAutocomplete-input": {
          flex: "0 0 16px",
          minWidth: 16,
          width: 16,
          padding: "0 !important",
          margin: "0 !important",
          opacity: 0,
          caretColor: "transparent",
        },

        "& input": {
          caretColor: "transparent",
        },

        ...props.sx,
      }}
    />
  );
}

dayjs.extend(customParseFormat);

type DateFilterInputProps<RecordType extends RaRecord = any> =
  InputProps<RecordType> & {
    displayFormat?: string;
    sx?: object;
  };

function toDayjsDate(value: unknown): Dayjs | null {
  if (value == null || value === "") return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  if (typeof value === "string") {
    // preferred filter storage format
    const iso = dayjs(value, "YYYY-MM-DD", true);
    if (iso.isValid()) return iso;

    // allow typed dd/mm/yyyy as fallback
    const de = dayjs(value, "DD/MM/YYYY", true);
    if (de.isValid()) return de;

    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  }

  return null;
}

export function DateFilterInput<RecordType extends RaRecord = any>({
  displayFormat = "DD/MM/YYYY",
  sx,
  source,
  validate,
  disabled,
  ...props
}: DateFilterInputProps<RecordType>) {
  const {
    field,
    fieldState: { error, invalid },
    id,
    isRequired,
  } = useInput({ source, validate, disabled, ...props });
  const value = toDayjsDate(field.value);
  const showError = invalid && !!error?.message;
  return (
    <DatePicker
      value={value}
      onChange={(newValue) => {
        if (!newValue || !newValue.isValid()) {
          field.onChange(null);
          return;
        }
        field.onChange(newValue.format("YYYY-MM-DD"));
      }}
      format={displayFormat}
      disabled={disabled}
      slotProps={{
        field: {
          clearable: true,
          onClear: () => field.onChange(null),
        },
        textField: {
          id,
          label: props.label,
          required: isRequired,
          size: "small",
          error: showError,
          helperText: false,
          onBlur: field.onBlur,
          sx: {
            width: layout.list.dateWidth,
            maxWidth: "100%",
            ...compactFieldSx,
            ...sx,
            "& .MuiInputAdornment-root": {
              minWidth: 56, // enough space for both icons
              justifyContent: "flex-end",
            },
          },
          InputLabelProps: { shrink: true },
        },
      }}
    />
  );
}
